import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ICustomerPortalService,
    Policy,
    PRODUCT_WOLF_MOTOR,
    ProductsEnum,
    resourcesHelper,
    Types
} from "@falcon/services";
import {finalize} from "rxjs/operators";
import {
    formatDateTime,
    getCoverTierWording} from "../../../../../CommonFunctions";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {Link} from "react-router-dom";


interface IOldPoliciesPageProps {
}

interface IOldPoliciesPageState {
    isLoading: boolean;
    isLoadingHistory: boolean;

    expiredPolicies: Policy[];
    hasOldHomePolicies: boolean;
}

@authorize
export class OldMotorPoliciesPage extends React.Component<IOldPoliciesPageProps, IOldPoliciesPageState> {
    private static readonly OLD_POLICY_BEFORE_RENEWAL: string = "OLD_POLICY_BEFORE_RENEWAL";

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IOldPoliciesPageProps) {
        super(props);

        this.state = {
            isLoading: false,
            isLoadingHistory: false,
            expiredPolicies: [],
            hasOldHomePolicies: false,
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Old Policies");

        this.setState({isLoading: true}, () =>
            this.customerPortalService
                .getExpiredPolicies()
                .pipe(finalize(() => this.setState({isLoading: false})))
                .subscribe((policies: Policy[]) => {
                    const hasOldHomePolicies = policies.filter(p => p.products.includes(ProductsEnum.HOME)).length > 0
                    const motorPolicies = policies.filter(p => p.products.includes(ProductsEnum.WOLF_MOTOR))
                    const joinPolicies = this.state.expiredPolicies.concat(motorPolicies);
                    this.setState({
                        expiredPolicies: joinPolicies || [],
                        hasOldHomePolicies,
                    });
                }),
        );

        this.setState({isLoadingHistory: true}, () =>
            this.customerPortalService.getPolicyHistoryAfterRenewal()
                .pipe(finalize(() => this.setState({isLoadingHistory: false})))
                .subscribe((policyHistories: Policy[]) => {
                    const carPoliciesBeforeRenewal = policyHistories.filter(p => p.products.includes(PRODUCT_WOLF_MOTOR));
                    const joinPolicies = this.state.expiredPolicies.concat(carPoliciesBeforeRenewal);
                    this.setState({
                        expiredPolicies: joinPolicies || []
                    });
                })
        );
    }

    public render() {
        return (
            <>
                <FalconHeader/>
                <Overlay show={this.state.isLoading || this.state.isLoadingHistory}/>
                <div className="page-contents">
                    {this.pluginTemplates.renderBackLink("Back", true, this.state.hasOldHomePolicies ? `/dashboard/annualOldPolicies` : `/dashboard`)}
                    <h1 className="page-heading-white">Your previous Allianz car policies</h1>
                    {(!this.state.isLoading && this.state.expiredPolicies.length > 0) && this.renderPolicies()}
                    {this.pluginTemplates.renderBackLink("Back", true, this.state.hasOldHomePolicies ? `/dashboard/annualOldPolicies` : `/dashboard`)}
                </div>
                <FalconFooter/>
            </>
        );
    }

    private renderPolicies = () => {
        if (this.state.expiredPolicies?.length < 1) {
            return (
                <div className="panel">
                    <p className="m-0 font-weight-extra-light">You don't have any previous policies</p>
                </div>
            );
        }

        return this.state.expiredPolicies.map((policy: Policy, index: number) => {
            const vehicles: any = [];
            for (const vehicle of policy.interaction.vehicles) {
                vehicles.push(
                    <div key={policy.policyNumber} id={`old-vehicle-${index}`}
                         className="section-panel your-old-car-cover-panel">
                        <div className="section car-details">
                            <img className="main-icon car-front-icon"
                                 src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`} alt="Vehicle icon"/>
                            <div className="section-heading car-details-heading">{vehicle.make}, {vehicle.model}</div>
                            <div className="section-contents">
                                <div className="detail registration-number">
                                    <div id={`old-vehicle-details-${index}`} className="number-plate-preview">
                                        {vehicle.registrationNumber}
                                    </div>
                                </div>
                                {policy.status === OldMotorPoliciesPage.OLD_POLICY_BEFORE_RENEWAL && (
                                    <div className="detail">
                                        <div className="detail-key">Status</div>
                                        <div id={`old-vehicle-status-${index}`}
                                             className="detail-value">Expired
                                        </div>
                                    </div>
                                )}
                                <div className="detail cover-start-date">
                                    <div className="detail-key">Cover start date:</div>
                                    <div id={`old-vehicle-start-date-${index}`}
                                         className="detail-value">{formatDateTime(policy.startDate)}</div>
                                </div>
                                <div className="detail">
                                    <div className="detail-key">Cover expiry date:</div>
                                    <div id={`old-vehicle-end-date-${index}`}
                                         className="detail-value">{formatDateTime(policy.endDate)}</div>
                                </div>
                                <div className="detail">
                                    <div className="detail-key">Cover level:</div>
                                    <div id={`old-vehicle-covel-level-${index}`}
                                         className="detail-value">{getCoverTierWording(policy.interaction.coverTier)}</div>
                                </div>

                                {policy.status === OldMotorPoliciesPage.OLD_POLICY_BEFORE_RENEWAL && (
                                    <>
                                        <div className="detail">
                                            <div className="detail-key">Driver(s)</div>
                                            {this.renderDrivers(policy, vehicle)}
                                        </div>
                                        <div className="section-links">
                                            <Link to={"/dashboard/pickAnnualMotorClaimKind"}
                                                  id={`makeClaim${index}`}
                                                  className={`buttonLink`}>
                                                <button type="button"
                                                        className={`btn btn-primary btn-next plugin`}>
                                                    Make a claim
                                                </button>
                                            </Link>
                                        </div>
                                    </>
                                )}
                            </div>
                            {policy.status !== OldMotorPoliciesPage.OLD_POLICY_BEFORE_RENEWAL && (
                                <div className="section-links">
                                    <ButtonLink
                                        id={`old-vehicle-summary-btn-${index}`}
                                        primary={true}
                                        to={`/dashboard/annualMotorOldPolicies/${policy.policyNumber}`}>
                                        View policy details
                                    </ButtonLink>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            return vehicles;
        });
    };

    private renderDrivers(policy: any, vehicle: any): JSX.Element {
        return (
            <>
                <div className="detail-value">
                    {getDrivers(vehicle)}
                </div>
            </>
        );

        function getDrivers(vehicle: any) {
            const drivers: any = [];
            if (vehicle.mainDriverId) {
                drivers.push(
                    <div key={vehicle.mainDriverId}>{getDriverInfo(vehicle.mainDriverId)} (main driver)</div>
                );
            }

            for (const driverId of vehicle.driverIds) {
                if (policy.interaction.additionalDrivers && policy.interaction.additionalDrivers.length !== 0
                    && driverId !== vehicle.mainDriverId) {
                    drivers.push(
                        <div key={driverId}>
                            {getDriverInfo(driverId)}
                        </div>
                    );
                }
            }
            return drivers;
        }

        function getDriverInfo(driverId: any) {
            if (driverId === policy.interaction.policyHolder.personId) {
                return (
                    <span>{policy.interaction.policyHolder.firstName} {policy.interaction.policyHolder.lastName}</span>
                );
            }
            let driverInfo;
            for (const driver of policy.interaction.additionalDrivers) {
                if (driverId === driver.personId) {
                    driverInfo =
                        <span>{driver.firstName} {driver.lastName}</span>
                }
            }
            return driverInfo;
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/oldPolicies/OldMotorPoliciesPage.tsx